import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X, Crown } from "lucide-react";

// localStorage here holds ONLY a non-sensitive UI preference (dismissal
// timestamp). Never store tokens or personal data in localStorage — auth
// lives in httpOnly cookies.
const DISMISS_KEY = "kingly.installPrompt.dismissedAt";
const DISMISS_DAYS = 14; // re-suggest after two weeks

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    if (!ts) return false;
    const ageMs = Date.now() - ts;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Install-to-home-screen prompt.
 * - Android / desktop Chrome: listens for beforeinstallprompt and offers a native install.
 * - iOS Safari: shows the manual Share → Add to Home Screen instructions (no API exists).
 * Auto-hidden when the app is already installed (running in display-mode: standalone)
 * or recently dismissed.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    function onBeforeInstallPrompt(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);

    // iOS Safari never fires beforeinstallprompt. Show a contextual hint after a beat.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      console.warn("localStorage unavailable", e);
    }
    setVisible(false);
    setShowIosHelp(false);
  }

  async function install() {
    if (!deferredPrompt) {
      // iOS path → show inline help
      setShowIosHelp(true);
      return;
    }
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) {
      console.warn("install prompt userChoice failed", e);
    }
    setDeferredPrompt(null);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      className="fixed left-3 right-3 bottom-[72px] md:hidden z-50 animate-in fade-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt"
    >
      <div className="bg-slate-900 text-white rounded-xl shadow-2xl overflow-hidden border border-slate-700">
        <div className="flex items-start gap-3 p-4">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-400 to-amber-700 flex items-center justify-center shrink-0">
            <Crown className="w-6 h-6 text-slate-900" fill="currentColor" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-white">
              Install Kingly
            </div>
            <p className="text-xs text-slate-300 mt-0.5">
              Add to your home screen for fast one-tap access during shows.
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-slate-400 hover:text-white shrink-0 -mr-1 -mt-1 p-1"
            aria-label="Dismiss"
            data-testid="install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {showIosHelp ? (
          <div className="px-4 pb-4 pt-1 text-xs text-slate-200 space-y-2" data-testid="install-ios-help">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-700 text-[10px] flex items-center justify-center font-semibold">1</span>
              <span className="flex-1">Tap the <Share className="w-3.5 h-3.5 inline -mt-0.5" /> share icon in Safari.</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-700 text-[10px] flex items-center justify-center font-semibold">2</span>
              <span className="flex-1">Choose <strong>Add to Home Screen</strong> <Plus className="w-3.5 h-3.5 inline -mt-0.5" />.</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-700 text-[10px] flex items-center justify-center font-semibold">3</span>
              <span className="flex-1">Tap <strong>Add</strong> — Kingly will appear on your home screen.</span>
            </div>
          </div>
        ) : (
          <div className="px-4 pb-3 flex gap-2">
            <Button
              onClick={install}
              className="flex-1 h-10 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="install-confirm"
            >
              <Download className="w-4 h-4 mr-2" />
              {isIos() ? "How to install" : "Install"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
